import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface Props {
    open: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    /**
     * Feste Fussleiste AUSSERHALB des Bildlaufs (z. B. „Fertig"). Innerhalb
     * des Bildlaufs klebte ein `sticky bottom-0` wegen des Safe-Area-Abstands
     * eine Handbreit ueber dem Rand, und darunter lief Inhalt durch.
     */
    footer?: React.ReactNode;
}

const SHEET_STATE = { osiaSheet: 'notifications' };

/**
 * Handy-Blatt ueber der ganzen Seite. Schliesst mit Esc, mit dem X — und mit
 * der Zurueck-Geste: beim Oeffnen wird ein Verlaufseintrag gelegt, `popstate`
 * schliesst. Wer stattdessen das X drueckt, bekommt den Eintrag wieder
 * abgeraeumt, sonst fuehrte das naechste „Zurueck" ins Leere.
 */
export const NotificationSheet: React.FC<Props> = ({ open, onClose, title = 'Mitteilungen', children, footer }) => {
    // Der Haupteffekt darf bei einem neuen onClose NICHT neu laufen (er legt
    // den Verlaufseintrag) — deshalb die Ref, im eigenen Effekt nachgezogen.
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

    useEffect(() => {
        if (!open) return;
        let closedByPop = false;
        try { window.history.pushState(SHEET_STATE, ''); } catch { /* Verlauf gesperrt — dann nur Esc/X */ }
        const onPop = () => { closedByPop = true; onCloseRef.current(); };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCloseRef.current(); };
        window.addEventListener('popstate', onPop);
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('popstate', onPop);
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
            const st = window.history.state as { osiaSheet?: string } | null;
            if (!closedByPop && st?.osiaSheet === SHEET_STATE.osiaSheet) {
                try { window.history.back(); } catch { /* egal */ }
            }
        };
    }, [open]);

    if (!open) return null;

    return (
        <div role="dialog" aria-modal="true" aria-label={title} className="fixed inset-0 z-[120] bg-white flex flex-col animate-fade-in">
            <div className="shrink-0 flex items-center justify-between gap-3 px-4 border-b border-slate-200" style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))', paddingBottom: '0.5rem' }}>
                <h2 className="text-base font-semibold text-slate-900">{title}</h2>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Schließen"
                    className="w-11 h-11 -mr-2 inline-flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 active:bg-slate-200"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto" style={{ paddingBottom: footer ? undefined : 'max(1rem, env(safe-area-inset-bottom))' }}>
                {children}
            </div>
            {footer && (
                <div className="shrink-0 bg-white border-t border-slate-200 px-4 pt-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
                    {footer}
                </div>
            )}
        </div>
    );
};
